import { DestroyRef, inject } from '@angular/core';

/**
 * JSON for comparing form values: a Set compares by its contents (property
 * types keep descriptors in one), and inside an object an unticked box or an
 * emptied field (false, '', null) counts the same as one never touched.
 */
function replacer(this: unknown, key: string, v: unknown): unknown {
  if (v instanceof Set) return [...v].sort();
  if (key !== '' && !Array.isArray(this) && (v === false || v === '' || v === null)) return undefined;
  return v;
}
const json = (value: unknown): string => JSON.stringify(value, replacer) ?? 'undefined';

/**
 * One editor section's form, kept apart from the record it was loaded from.
 * After any save the editor refetches the whole record; `load` then refreshes
 * only the fields the admin hasn't touched, so saving one part doesn't wipe
 * unsaved edits in another. `read`/`write` wrap the section's own fields.
 */
export class FormDraft<T extends object> {
  /** Each field as last loaded, as JSON so later typing can't change it. */
  private loaded?: Map<keyof T, string>;
  private readonly accepted = new Set<keyof T>();

  constructor(
    private readonly read: () => T,
    private readonly write: (value: Partial<T>) => void,
  ) {}

  /** The record arrived (first load, or a refetch after any save). */
  load(next: T): void {
    const keys = Object.keys(next) as (keyof T)[];
    const current = this.read();
    const loaded = this.loaded;
    const fresh = loaded ? keys.filter((k) => this.accepted.has(k) || json(current[k]) === loaded.get(k)) : keys;
    this.write(Object.fromEntries(fresh.map((k) => [k, next[k]])) as Partial<T>);
    this.loaded = new Map(keys.map((k) => [k, json(next[k])]));
    this.accepted.clear();
  }

  /** These fields were just saved: take whatever the server sends back next. */
  accept(...keys: (keyof T)[]): void {
    keys.forEach((k) => this.accepted.add(k));
  }

  /** The whole form was just saved. */
  acceptAll(): void {
    this.loaded?.forEach((_, k) => this.accepted.add(k));
  }

  /** True when any field differs from what was last loaded. */
  get dirty(): boolean {
    const loaded = this.loaded;
    if (!loaded) return false;
    const current = this.read();
    return [...loaded].some(([k, v]) => json(current[k]) !== v);
  }
}

/** The drafts open in one editor, so it can tell whether anything is unsaved. */
export class FormDrafts {
  private readonly drafts = new Set<{ readonly dirty: boolean }>();

  /** Call from a section's field initializer: the draft is dropped when the section closes. */
  track<T extends object>(read: () => T, write: (value: Partial<T>) => void): FormDraft<T> {
    const draft = new FormDraft(read, write);
    this.drafts.add(draft);
    inject(DestroyRef).onDestroy(() => this.drafts.delete(draft));
    return draft;
  }

  get dirty(): boolean {
    return [...this.drafts].some((d) => d.dirty);
  }
}
